import { forwardRef } from "react";
import { InputAdornment, TextField, TextFieldProps } from "@mui/material";
import CalendarCheckFillIcon from "remixicon-react/CalendarCheckFillIcon";
import dateformat from "dateformat";
import { useCommonStyles } from "$containers/CommonStyles/CommonStyles";

interface DateInputProps extends Omit<TextFieldProps, "onChange"> {
  startDate?: Date | null;
  endDate?: Date | null;
  onClear?: () => void;
  displayCalendarIcon?: boolean;
  variant?: "filled" | "outlined" | "standard";
  error?: boolean;
}

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      startDate,
      endDate,
      onClear: _onClear,
      label,
      displayCalendarIcon = true,
      variant = "outlined",
      error,
      // react-datepicker injects `value`; prefer our formatted display value.
      value: _datepickerValue,
      ...props
    },
    ref
  ) => {
    const commonClasses = useCommonStyles();

    // Always a string — `value={null}` makes MUI InputBase controlled+empty in a
    // way that can thrash FormControl onEmpty/onFilled under React 19.
    let inputDateValue = "";
    if (startDate) {
      inputDateValue = dateformat(startDate, "dd/mm/yyyy");
      if (endDate && startDate !== endDate) {
        inputDateValue += ` - ${dateformat(endDate, "dd/mm/yyyy")}`;
      }
    }

    return (
      <TextField
        ref={ref}
        {...props}
        value={inputDateValue}
        placeholder=" -- / -- / ----"
        className={[commonClasses.select].join(" ")}
        variant={variant || "outlined"}
        label={label}
        error={!!error}
        InputLabelProps={{
          shrink: true,
        }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {displayCalendarIcon && (
                <CalendarCheckFillIcon
                  color="#646262"
                  style={{ marginLeft: "3px" }}
                />
              )}
            </InputAdornment>
          ),
        }}
      />
    );
  }
);

export default DateInput;
